import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, Button, IconButton, TextField, Tooltip } from '@mui/material'
import { Check, PersonRemove } from '@mui/icons-material'
import { Popconfirm, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import { debounce } from 'lodash'
import { trackPromise } from 'react-promise-tracker'
import services from '../../services/index.js'
import { useNotification } from '../../hooks/useNotification.js'

interface Liberacao {
  usuarioId: number
  nome: string
  email?: string | null
  limiteMensal?: number | null
  usadoNoMes: number
}

interface Pessoa {
  id: number
  name: string
  email?: string
}

const inteiro = new Intl.NumberFormat('pt-BR')

const lerLimite = (texto: string): number | null | 'invalido' => {
  if (texto.trim() === '') return null
  const n = Number(texto)
  return Number.isInteger(n) && n > 0 ? n : 'invalido'
}

function LimiteEditavel({ item, aoSalvar }: { item: Liberacao; aoSalvar: (limite: number | null) => void }) {
  const original = item.limiteMensal != null ? String(item.limiteMensal) : ''
  const [texto, setTexto] = useState(original)
  useEffect(() => setTexto(original), [original])
  const valor = lerLimite(texto)
  const mudou = texto.trim() !== original

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <TextField size="small" type="number" value={texto} placeholder="Sem limite" sx={{ width: 140 }}
                 error={valor === 'invalido'} onChange={e => setTexto(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter' && mudou && valor !== 'invalido') aoSalvar(valor) }} />
      {mudou && (
        <Tooltip title="Salvar limite">
          <span>
            <IconButton size="small" disabled={valor === 'invalido'} onClick={() => valor !== 'invalido' && aoSalvar(valor)}>
              <Check fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  )
}

/** Quem pode perguntar a este agente, com o limite de cada pessoa e o uso do mês. */
export default function Liberacoes({ agenteId }: { agenteId: number }) {
  const { notify: notificar } = useNotification()
  const notificarRef = useRef(notificar)
  notificarRef.current = notificar
  const notify = useCallback((m: string, o?: any) => notificarRef.current(m, o), [])
  const [lista, setLista] = useState<Liberacao[]>([])
  const [busca, setBusca] = useState('')

  const [opcoes, setOpcoes] = useState<Pessoa[]>([])
  const [buscandoPessoas, setBuscandoPessoas] = useState(false)
  const [pessoa, setPessoa] = useState<Pessoa | null>(null)
  const [limiteNovo, setLimiteNovo] = useState('')

  const carregar = useCallback(async () => {
    const res = await trackPromise(services.api.oneflowLiberacoes(agenteId))
    if (res.fail) {
      notify(res.error || 'Não foi possível consultar as liberações', { variant: 'error' })
      return
    }
    setLista(res.data || [])
  }, [agenteId, notify])

  useEffect(() => { carregar() }, [carregar])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const procurarPessoas = useCallback(debounce(async (termo: string) => {
    if (termo.trim().length < 2) { setOpcoes([]); setBuscandoPessoas(false); return }
    const res = await services.api.getUsersAll({ adicionalQuery: `?fullname=${encodeURIComponent(termo.trim())}` }, 1, 20)
    setBuscandoPessoas(false)
    if (res.fail) return
    setOpcoes(res.data?.data ?? res.data ?? [])
  }, 500), [])

  const liberar = useCallback(async (usuarioId: number, limite: number | null, aviso: string) => {
    const res = await trackPromise(services.api.oneflowLiberar(agenteId, usuarioId, limite))
    if (res.fail) {
      notify(res.error || 'Não foi possível salvar a liberação', { variant: 'error' })
      return false
    }
    notify(aviso, { variant: 'success' })
    await carregar()
    return true
  }, [agenteId, carregar, notify])

  const tirar = useCallback(async (usuarioId: number) => {
    const res = await trackPromise(services.api.oneflowTirarLiberacao(agenteId, usuarioId))
    if (res.fail) {
      notify(res.error || 'Não foi possível remover a liberação', { variant: 'error' })
      return
    }
    setLista(l => l.filter(x => x.usuarioId !== usuarioId))
  }, [agenteId, notify])

  const limiteNovoValor = lerLimite(limiteNovo)
  const jaLiberada = pessoa != null && lista.some(l => l.usuarioId === pessoa.id)

  const adicionar = async () => {
    if (!pessoa || limiteNovoValor === 'invalido') return
    if (await liberar(pessoa.id, limiteNovoValor, `${pessoa.name} liberada`)) {
      setPessoa(null)
      setLimiteNovo('')
    }
  }

  const filtrada = useMemo(() => {
    const t = busca.trim().toLowerCase()
    return t ? lista.filter(l => `${l.nome} ${l.email || ''}`.toLowerCase().includes(t)) : lista
  }, [lista, busca])

  const colunas: TableColumnsType<Liberacao> = [
    {
      title: 'Pessoa',
      key: 'pessoa',
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{r.nome}</span>
          {r.email && <span style={{ fontSize: 12, color: '#6b7a80' }}>{r.email}</span>}
        </div>
      )
    },
    {
      title: 'Usado no mês',
      key: 'usado',
      align: 'right',
      sorter: (a, b) => a.usadoNoMes - b.usadoNoMes,
      render: (_, r) => {
        const pct = r.limiteMensal ? Math.round((r.usadoNoMes / r.limiteMensal) * 100) : null
        return (
          <span>
            {inteiro.format(r.usadoNoMes)}
            {pct !== null && <span style={{ fontSize: 12, color: pct >= 90 ? '#b45309' : '#6b7a80' }}> ({pct}%)</span>}
          </span>
        )
      }
    },
    {
      title: 'Limite mensal (tokens)',
      key: 'limite',
      render: (_, r) => <LimiteEditavel item={r} aoSalvar={l => liberar(r.usuarioId, l, 'Limite atualizado')} />
    },
    {
      title: '',
      key: 'acoes',
      align: 'right',
      render: (_, r) => (
        <Popconfirm title={`Tirar ${r.nome} do oneflow deste agente?`} okText="Tirar" cancelText="Cancelar"
                    onConfirm={() => tirar(r.usuarioId)}>
          <Tooltip title="Tirar liberação">
            <IconButton size="small"><PersonRemove fontSize="small" /></IconButton>
          </Tooltip>
        </Popconfirm>
      )
    }
  ]

  return (
    <>
      <h2>Quem pode usar</h2>
      <p style={{ fontSize: 13, color: '#6b7a80', marginTop: 0 }}>
        Administradores do BI sempre podem perguntar. As demais pessoas precisam estar liberadas aqui e ter acesso ao
        dashboard. O limite é por pessoa, em tokens por mês; vazio usa só o limite do agente.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 16 }}>
        <Autocomplete
          sx={{ width: 340 }}
          size="small"
          options={opcoes}
          value={pessoa}
          loading={buscandoPessoas}
          filterOptions={x => x}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText="Digite ao menos 2 letras do nome"
          loadingText="Procurando..."
          onChange={(_, v) => setPessoa(v)}
          onInputChange={(_, v, motivo) => {
            if (motivo !== 'input') return
            setBuscandoPessoas(true)
            procurarPessoas(v)
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{o.name}</span>
                {o.email && <span style={{ fontSize: 12, color: '#6b7a80' }}>{o.email}</span>}
              </div>
            </li>
          )}
          renderInput={params => <TextField {...params} label="Liberar pessoa" placeholder="Nome da pessoa" />}
        />
        <TextField size="small" type="number" label="Limite mensal" placeholder="Sem limite" sx={{ width: 160 }}
                   value={limiteNovo} error={limiteNovoValor === 'invalido'} onChange={e => setLimiteNovo(e.target.value)}
                   InputLabelProps={{ shrink: true }} />
        <Button variant="contained" color="secondary" disabled={!pessoa || jaLiberada || limiteNovoValor === 'invalido'}
                onClick={adicionar} sx={{ height: 40 }}>
          {jaLiberada ? 'Já liberada' : 'Liberar'}
        </Button>
        <div style={{ flex: 1 }} />
        <TextField size="small" label="Buscar nas liberações" value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <Table
        size="small"
        columns={colunas}
        dataSource={filtrada}
        rowKey={r => String(r.usuarioId)}
        pagination={filtrada.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
        locale={{ emptyText: busca ? 'Ninguém encontrado' : 'Ninguém liberado ainda' }}
      />
    </>
  )
}
